import type { VideoModel } from '..';
import {
    BaseVideoProvider,
    type VideoProviderAdapter,
    TaskStatus,
    type CreateTaskConfig,
    type CreateTaskResult,
    type PollTaskConfig,
    type PollTaskResult,
} from '../base';
import { buildInputPayloadFromConfig, getvolcengineModelConfig } from './utils';
import { volcengineApiPaths, volcengineStatusMapping } from './config';
import type { volcengineCreateResponse, volcengineTaskResponse } from './types';
import { getValueByPath, isStatusMatch } from '../tools';

const DEFAULT_BASE_URL_ENV = 'VOLCENGINE_BASE_URL';
const DEFAULT_API_KEY_ENV = 'VOLCENGINE_API_KEY';

const providerCache = new Map<string, BaseVideoProvider>();

function getOrCreateProvider(baseUrlEnv: string, apiKeyEnv: string): BaseVideoProvider {
    const cacheKey = `${baseUrlEnv}::${apiKeyEnv}`;
    let provider = providerCache.get(cacheKey);
    if (!provider) {
        provider = new BaseVideoProvider(
            { baseUrlEnv, apiKeyEnv, debugEnv: 'VOLCENGINE_DEBUG', providerName: 'VOLCENGINE' },
        );
        providerCache.set(cacheKey, provider);
    }
    return provider;
}

// 实现适配器
const adapter: VideoProviderAdapter = {
    // 构建**任务创建**请求
    async buildCreateTaskRequest(modelId, params, context): Promise<CreateTaskConfig> {
        // 获取模型配置
        const apiPath = volcengineApiPaths.createTask;
        // 构建输入参数（可能是异步构建）
        const payload = await buildInputPayloadFromConfig(modelId, params);
        // 返回请求配置
        return {
            url: `${context.baseUrl}${apiPath}`,
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${context.apiKey}`,
            },
            body: payload,
        };
    },

    // 解析**任务创建**响应
    parseCreateTaskResponse(modelId, response, data: volcengineCreateResponse): CreateTaskResult {
        // 获取任务 ID
        const taskId = getValueByPath(data, volcengineStatusMapping.taskIdPath);
        if (!taskId) {
            throw new Error(`Failed to get task id from response${data.msg ? `: ${data.msg}` : ''}`);
        }
        // 返回任务 ID
        return {
            taskId: taskId,
            raw: data,
        };
    },

    // 构建**轮询任务**请求
    buildPollTaskRequest(modelId, taskId, params, context): PollTaskConfig {
        // 返回请求配置
        return {
            url: (taskId) => `${context.baseUrl}${volcengineApiPaths.pollTask}/${taskId}`,
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${context.apiKey}`,
            },
        };
    },

    // 解析**轮询任务**响应
    parsePollTaskResponse(modelId, response, data: volcengineTaskResponse): PollTaskResult {
        // 获取任务状态
        const state = getValueByPath(data, volcengineStatusMapping.statePath);
        // 如果状态值未找到则视为**处理中**
        if (state === undefined) {
            return {
                status: TaskStatus.PROCESSING,
                raw: data,
            };
        }
        // 1. 判断**成功**状态
        if (isStatusMatch(state, volcengineStatusMapping.success)) {
            const videoUrl = getValueByPath(data, volcengineStatusMapping.videoUrlPath);
            // 视频 URL 不存在则视为**失败**状态
            if (!videoUrl) {
                return {
                    status: TaskStatus.FAILED,
                    error: 'Missing video URL in result',
                    raw: data,
                };
            }
            // 返回**成功**状态
            return {
                status: TaskStatus.SUCCESS,
                videoUrl,
                raw: data,
            };
        }

        // 2. 判断**失败**状态
        if (isStatusMatch(state, volcengineStatusMapping.failed)) {
            const error = getValueByPath(data, volcengineStatusMapping.errorPath);
            // 返回**失败**状态
            return {
                status: TaskStatus.FAILED,
                error: error != null ? String(error) : 'unknown error',
                raw: data,
            };
        }

        // 3. 判断**处理中**状态
        if (isStatusMatch(state, volcengineStatusMapping.processing)) {
            // 返回**处理中**状态
            return {
                status: TaskStatus.PROCESSING,
                raw: data,
            };
        }

        // 4. 判断**未知**状态 (不在配置范围内)
        // 返回**处理中**状态
        return {
            status: TaskStatus.PROCESSING,
            raw: data,
        };
    },
};

// 导出工厂函数
export const volcengine = (modelId: string): VideoModel => {
    const config = getvolcengineModelConfig(modelId);
    const baseUrlEnv = config.baseUrlEnv ?? DEFAULT_BASE_URL_ENV;
    const apiKeyEnv = config.apiKeyEnv ?? DEFAULT_API_KEY_ENV;
    const provider = getOrCreateProvider(baseUrlEnv, apiKeyEnv);
    return provider.createVideoModel(modelId, adapter);
};